import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { EVENTS, STATUS, ACTIONS, type EventData } from 'react-joyride';
import { useAuth } from '../context/AuthContext';
import { WELCOME_TOUR_STEPS } from '../components/onboarding/welcomeTourSteps';

const STORAGE_PREFIX = 'tour_bienvenida_v1';
const seenKey = (idUsuario: string) => `${STORAGE_PREFIX}_${idUsuario}`;
const hintKey = (idUsuario: string) => `${STORAGE_PREFIX}_${idUsuario}_hint`;

function readFlag(key: string): boolean {
  try { return localStorage.getItem(key) === '1'; } catch { return false; }
}
function writeFlag(key: string, value: boolean): void {
  try {
    if (value) localStorage.setItem(key, '1');
    else localStorage.removeItem(key);
  } catch { /* localStorage no disponible: degradación aceptable */ }
}

export interface UseWelcomeTourReturn {
  readonly run: boolean;
  readonly stepIndex: number;
  /** Relanza el tour desde el paso 1 (desde el ícono de ayuda). */
  readonly relaunch: () => void;
  /** true = mostrar indicador "descubrible" en el ícono de ayuda. */
  readonly helpHintPending: boolean;
  /** Apaga el indicador descubrible (al abrir el menú de ayuda). */
  readonly dismissHelpHint: () => void;
  /** Handler para la prop onEvent de <Joyride>. */
  readonly onEvent: (data: EventData) => void;
}

export function useWelcomeTour(): UseWelcomeTourReturn {
  const { usuario } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const idUsuario = usuario?.id ?? null;

  const [run, setRun] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);
  const [helpHintPending, setHelpHintPending] = useState(false);
  // Índice al que reiniciar una vez que `run` termine de apagarse (ver más abajo).
  const [reinicioPendiente, setReinicioPendiente] = useState<number | null>(null);

  // Auto-invitación en el primer ingreso (una vez que hay usuario cargado).
  useEffect(() => {
    if (!idUsuario) return;
    const yaVisto = readFlag(seenKey(idUsuario));
    setHelpHintPending(readFlag(hintKey(idUsuario)));
    if (!yaVisto) {
      setStepIndex(0);
      setRun(true);
    }
  }, [idUsuario]);

  const marcarVisto = useCallback((activarHint: boolean) => {
    if (!idUsuario) return;
    writeFlag(seenKey(idUsuario), true);
    if (activarHint) {
      writeFlag(hintKey(idUsuario), true);
      setHelpHintPending(true);
    }
    setRun(false);
  }, [idUsuario]);

  const relaunch = useCallback(() => {
    navigate('/lumisial/home');
    setStepIndex(0);
    setRun(true);
  }, [navigate]);

  const dismissHelpHint = useCallback(() => {
    if (!idUsuario) return;
    writeFlag(hintKey(idUsuario), false);
    setHelpHintPending(false);
  }, [idUsuario]);

  // react-joyride ignora los cambios de `stepIndex` una vez que su estado interno
  // queda en SKIPPED/FINISHED (solo el flanco false→true de `run` fuerza un `start()`
  // real en el índice nuevo). Por eso redirigir al paso de cierre exige apagar `run`
  // primero y recién, cuando el próximo render confirma que ya está apagado, fijar el
  // índice de destino y volver a encenderlo.
  useEffect(() => {
    if (reinicioPendiente === null || run) return;
    setStepIndex(reinicioPendiente);
    setRun(true);
    setReinicioPendiente(null);
  }, [reinicioPendiente, run]);

  // Avance de pasos accionables: cuando el usuario NAVEGA a la ruta esperada.
  // Se exige un cambio real de ruta (no solo "la ruta ya coincide") para que "Atrás"
  // pueda volver a un paso accionable cuya ruta ya está activa sin que el efecto lo
  // vuelva a empujar hacia adelante de inmediato (loop).
  const rutaAnteriorRef = useRef(location.pathname);
  useEffect(() => {
    const cambioDeRuta = rutaAnteriorRef.current !== location.pathname;
    rutaAnteriorRef.current = location.pathname;
    if (!run || !cambioDeRuta) return;
    const paso = WELCOME_TOUR_STEPS[stepIndex];
    if (paso?.tipo === 'accionable' && location.pathname === paso.rutaEsperada) {
      setStepIndex((i) => Math.min(i + 1, WELCOME_TOUR_STEPS.length - 1));
    }
  }, [run, stepIndex, location.pathname]);

  const onEvent = useCallback((data: EventData) => {
    const { action, index, status, type } = data;

    // Salida (Salir / cerrar / ESC / completar).
    if (status === STATUS.SKIPPED) {
      const esUltimoPaso = index === WELCOME_TOUR_STEPS.length - 1;
      // Declinar la invitación inicial ("Ahora no") no cuenta como salida a medio
      // recorrido: cierra de inmediato, como siempre. Recién a partir de haber
      // empezado (índice > 0) tiene sentido redirigir al paso de cierre en vez de
      // cerrar de golpe — así siempre se explica dónde retomarlo.
      const salioAMedioRecorrido = index > 0 && !esUltimoPaso;
      if (salioAMedioRecorrido) { setRun(false); setReinicioPendiente(WELCOME_TOUR_STEPS.length - 1); return; }
      marcarVisto(true);
      return;
    }
    if (status === STATUS.FINISHED) { marcarVisto(false); return; } // completó → sin hint

    // Avance de pasos informativos (botón "Continuar"/"Empezar").
    if (type === EVENTS.STEP_AFTER || type === EVENTS.TARGET_NOT_FOUND) {
      // Al salir del paso 0 (bienvenida) hacia adelante, navegar a Home.
      if (index === 0 && action !== ACTIONS.PREV) {
        navigate('/lumisial/home');
      }
      // Aquí vivía un salto automático a otra pantalla al avanzar (`rutaAlAvanzar`), usado para
      // volver de Aportes a Balance. Se eliminó: el recorrido promete que los clics los da el
      // usuario, y esa navegación silenciosa hacía aparecer el paso siguiente sin que se
      // entendiera cómo se había llegado. Ahora ese regreso es un paso accionable más
      // (`pasoVolver`). No reintroducirlo: el mecanismo se ve inofensivo y no lo es.
      setStepIndex(index + (action === ACTIONS.PREV ? -1 : 1));
    }
  }, [marcarVisto, navigate]);

  return useMemo(() => ({
    run, stepIndex, relaunch, helpHintPending, dismissHelpHint, onEvent,
  }), [run, stepIndex, relaunch, helpHintPending, dismissHelpHint, onEvent]);
}
